import { C, TAGLINE_LONG } from "@/brand/tokens";
import { footerColumns } from "@/brand/content";
import { FaithMark } from "@/components/brand/FaithMark";
import { FaithMonogram } from "@/components/brand/FaithMonogram";

/**
 * SiteFooter — Smoked Ink chamber. Wordmark + tagline + 3 link columns
 * + closing rule with copyright and brass watermark. Restrained, anchored.
 *
 * Props:
 *   - stamp, labelSuffix: footer bottom-bar labels (per-concept identity)
 *   - markSubtitle: subtitle line under the FaithMark wordmark (defaults
 *     to legacy "Executive Advisory" so Concept 4 continues to render
 *     unchanged; Concept 5 passes "Leadership, People & Culture")
 *   - descriptor: the paragraph beneath the tagline (Concept 5 passes
 *     the consulting-firm framing)
 */
export function SiteFooter({
  stamp = "Concept 04",
  labelSuffix = "Synthesis",
  markSubtitle = "Executive Advisory",
  descriptor = "An advisory practice for mission-driven organizations carrying high-stakes people, leadership, and culture work.",
}) {
  return (
    <footer
      style={{
        background: C.bgDark,
        color: C.textOnDark,
        borderTop: `1px solid ${C.brassSoft}`,
      }}
    >
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-14 py-20">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-5">
              <FaithMonogram size={56} tone="primary" />
              <FaithMark size="md" tone="light" subtitle={markSubtitle} />
            </div>
            <p
              className="font-cormorant italic mt-9 max-w-xs"
              style={{ color: C.accentMetal, fontSize: "1.15rem" }}
            >
              {TAGLINE_LONG}
            </p>
            <p
              className="font-inter mt-6 max-w-xs leading-[1.7]"
              style={{ color: C.whiteOnDark, fontSize: "0.95rem" }}
            >
              {descriptor}
            </p>
          </div>

          <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-10">
            {footerColumns.map(([title, items]) => (
              <div key={title}>
                <div
                  className="font-inter text-[10.5px] uppercase font-semibold mb-6 pb-3"
                  style={{
                    color: C.accentMetal,
                    letterSpacing: "0.26em",
                    borderBottom: `1px solid ${C.brassSoft}`,
                  }}
                >
                  {title}
                </div>
                <ul className="space-y-3">
                  {items.map((i) => (
                    <li key={i}>
                      <a
                        href="#"
                        className="block font-inter text-[14px] transition-colors"
                        style={{ color: C.whiteOnDark }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = C.accentMetal)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = C.whiteOnDark)
                        }
                      >
                        {i}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div
          className="mt-16 pt-6 flex flex-wrap items-center justify-between gap-3 font-inter text-[10.5px] uppercase font-medium"
          style={{
            borderTop: `1px solid ${C.brassSoft}`,
            color: C.whiteOnDarkSoft,
            letterSpacing: "0.26em",
          }}
        >
          <span>
            © {new Date().getFullYear()} &nbsp;Faith 1st &nbsp;·&nbsp; {stamp}
          </span>
          <span style={{ color: C.accentMetal }}>{labelSuffix}</span>
        </div>
      </div>
    </footer>
  );
}
